import React from "react";
import { Check, ChevronDown, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";

export interface ColumnConfig {
  key: string;
  title: string;
  width: number;
  visible: boolean;
}

interface ColumnVisibilitySelectorProps {
  columns: ColumnConfig[];
  onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  visibleOverrideCount?: number;
}

export const ColumnVisibilitySelector: React.FC<ColumnVisibilitySelectorProps> = ({
  columns,
  onColumnVisibilityChange,
  onSelectAll,
  onSelectNone,
  visibleOverrideCount,
}) => {
  const visibleCount = visibleOverrideCount ?? columns.filter(col => col.visible).length;
  const totalCount = columns.length;
  const isAllSelected = visibleCount === totalCount;
  const isNoneSelected = visibleCount === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Eye className="h-4 w-4" />
          Colunas ({visibleCount}/{totalCount})
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-background border shadow-lg z-50">
        <div className="p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Visibilidade das Colunas</span>
          </div>
          
          <div className="flex gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAll}
              disabled={isAllSelected}
              className="flex-1 text-xs"
            >
              Todas
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectNone}
              disabled={isNoneSelected}
              className="flex-1 text-xs"
            >
              Nenhuma
            </Button>
          </div>

          <DropdownMenuSeparator />
        </div>

        <div className="max-h-80 overflow-y-auto">
          {columns.map((column) => (
            <DropdownMenuItem
              key={column.key}
              className="flex items-center gap-3 p-2 cursor-pointer hover:bg-muted"
              onClick={(e) => {
                e.preventDefault();
                onColumnVisibilityChange(column.key, !column.visible);
              }}
            >
              <Checkbox
                checked={column.visible}
                onCheckedChange={(checked) => {
                  onColumnVisibilityChange(column.key, !!checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex items-center gap-2 flex-1">
                {column.visible ? (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm">{column.title}</span>
              </div>
              {column.visible && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};